/**
 * `useDuplicateRequirement` — the hook around `duplicateRequirementFlow`, with the real API and
 * the cache invalidations a new enquiry + quotation call for.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost, callPut } from './bosun'
import { duplicateRequirementFlow, type DuplicateResult } from './duplicateRequirementFlow'
import { applyItemPatches, todayLocalDate, updateEnquiryItems } from './useEnquiryMutations'
import { findQuotationName } from './useQuotation'

export function useDuplicateRequirement(options?: { onSuccess?: (result: DuplicateResult) => void; onError?: (error: Error) => void }) {
  const queryClient = useQueryClient()
  return useMutation<DuplicateResult, Error, string>({
    mutationFn: (sourceId) =>
      duplicateRequirementFlow(sourceId, todayLocalDate(), {
        callGet,
        callPost,
        callPut,
        findQuotationName,
        updateLines: async (enquiryId, patches) => {
          const byLineId = new Map(patches.map((p) => [p.lineId, p.patch]))
          await updateEnquiryItems(enquiryId, (items) => applyItemPatches(items, byLineId))
        },
      }),
    onSuccess: (result) => {
      // The board gains a card; the new enquiry and its quotation are read fresh on arrival.
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      void queryClient.invalidateQueries({ queryKey: ['board-replies'] })
      options?.onSuccess?.(result)
    },
    onError: (error) => options?.onError?.(error),
  })
}
